<template>
    <div class="main-container">
        <!-- Video Upload Section -->
        <div class="create-video" v-if="showUploadSection">
            <h2>Upload video</h2>

            <!-- Authentication Check -->
            <div v-if="!isUserAuthenticated" class="auth-required">
                <div class="auth-icon">
                    <vue-feather type="lock" size="48" stroke-width="1"></vue-feather>
                </div>
                <h3>Authentication Required</h3>
                <p>You need to be logged in to upload videos.</p>
                <button @click="$router.push('/')" class="btn btn-primary">Go Home</button>
            </div>

            <!-- Upload Interface -->
            <div v-else>
                <div class="upload-field">
                    <div class="upload-button" v-if="!(uploading || uploaded)">
                        <input type="file" @change="onFileChange" accept="video/mp4" hidden id="upload-file" />
                        <label for="upload-file" class="btn btn_primary">Upload video</label>
                    </div>
                    <div class="upload-status" v-if="uploading">
                        Uploading {{ parseInt(uploadProgress) }}%...
                    </div>
                    <div class="upload-preview" v-if="uploaded">
                        Video is uploaded and processing...
                    </div>
                </div>
                <video-form
                    v-if="uploaded"
                    mode="create"
                    :video="newVideo"
                    :categories="categories"
                    :tags="videoTags"
                    :available-tags="availableTags"
                    :primary-category="primaryCategory"
                    :secondary-category="secondaryCategory"
                    :is-creating="creating"
                    :is-created="created"
                    @update:video="updateNewVideo"
                    @update:tags="updateVideoTags"
                    @update:primary-category="updatePrimaryCategory"
                    @update:secondary-category="updateSecondaryCategory"
                    @save="saveVideo"
                />
            </div>
        </div>

        <!-- Homepage Layout -->
        <template v-if="isHomePage && !showUploadSection">
            <!-- Featured Carousel -->
            <FeaturedCarousel />

            <!-- Homepage load failure (e.g. API outage / expired key) -->
            <api-error-state
                v-if="homeLoadError && !isLoadingRecent && !isLoadingTop && !isLoadingCategories"
                source="home_sections"
                @retry="loadHomepageData"
            />

            <!-- Recent Videos Section -->
            <video-section
                :title="currentLanguage === 'TA' ? 'சமீபத்தியவை' : 'Recent Videos'"
                :videos="recentVideos"
                :user-images="userImages"
                :loading="isLoadingRecent"
                :cols="4"
                :limit="8"
                icon="clock"
                icon-color="#e91e63"
                see-more-link="/latest"
            />

            <!-- Top Videos Section -->
            <video-section
                :title="currentLanguage === 'TA' ? 'சிறந்தவை' : 'Top Videos'"
                :videos="topVideos"
                :user-images="userImages"
                :loading="isLoadingTop"
                :cols="4"
                :limit="8"
                icon="trending-up"
                icon-color="#ff9800"
                see-more-link="/top"
            />
        </template>

        <!-- Other Routes (History, Top, etc.) -->
        <template v-if="!isHomePage && !showUploadSection">
            <!-- Loading Shimmer State -->
            <template v-if="isLoadingData">
                <!-- Channel grid shimmer for popular channels -->
                <div v-if="isPopularChannelsPage" class="shimmer-channels-grid">
                    <div v-for="n in 8" :key="'shimmer-ch-' + n" class="shimmer-channel-card">
                        <div class="shimmer-ch-avatar"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-ch-name"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-ch-username"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-ch-stats">
                            <div class="shimmer-ch-stat"><div class="shimmer-overlay"></div></div>
                            <div class="shimmer-ch-stat"><div class="shimmer-overlay"></div></div>
                        </div>
                    </div>
                </div>
                <!-- Video grid shimmer for video pages -->
                <div v-else class="shimmer-video-grid">
                    <div v-for="n in 12" :key="'shimmer-v-' + n" class="shimmer-video-card">
                        <div class="shimmer-thumb"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-card-info">
                            <div class="shimmer-card-avatar"><div class="shimmer-overlay"></div></div>
                            <div class="shimmer-card-text">
                                <div class="shimmer-card-title"><div class="shimmer-overlay"></div></div>
                                <div class="shimmer-card-title short"><div class="shimmer-overlay"></div></div>
                                <div class="shimmer-card-meta"><div class="shimmer-overlay"></div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Content Display -->
            <template v-if="!isLoadingData">
                <api-error-state v-if="loadError" source="video_grid" @retry="loadVideos" />
                <template v-else>
                    <video-grid
                        v-if="allVideos && !isPopularChannelsPage"
                        :cols="columnCount"
                        :videos="displayedGridVideos"
                        :user-images="userImages"
                    />

                    <!-- Load More -->
                    <div
                        v-if="allVideos && !isPopularChannelsPage && allVideosNextToken"
                        class="load-more-container"
                    >
                        <div v-if="isLoadingMoreVideos" class="load-more-loading">
                            <span class="load-more-spinner"></span>
                            <span>{{ currentLanguage === 'TA' ? 'ஏற்றுகிறது…' : 'Loading…' }}</span>
                        </div>
                        <button v-else class="load-more-button" @click="loadMoreVideos">
                            {{ currentLanguage === 'TA' ? 'மேலும் காண்பி' : 'Load More' }}
                        </button>
                    </div>

                    <channel-grid
                        v-if="isPopularChannelsPage && allChannels"
                        :channels="allChannels"
                    />
                </template>
            </template>
        </template>

        <VideoRecommendations
            v-if="!isPopularChannelsPage"
            :current-user="currentUser"
            :title="currentLanguage === 'TA' ? 'பரிந்துரைக்கப்பட்டவை ' : 'Recommended Videos'"
        />

        <!-- Top Categories - Video Grids -->
        <template v-if="isHomePage && !showUploadSection">
            <template v-for="(category, index) in allCategories" :key="'top-' + category.id">
                <video-section
                    :title="currentLanguage === 'TA' ? category.nameTA : (category.nameEN || category.nameTA)"
                    :videos="categoryVideos[category.id] || []"
                    :user-images="userImages"
                    :loading="isLoadingCategories"
                    :cols="4"
                    :limit="8"
                    icon="film"
                    :icon-color="categoryColors[index % categoryColors.length]"
                    :see-more-link="`/category/${category.id}`"
                    :has-more="!!categoryNextTokens[category.id]"
                    :loading-more="!!categoryLoadingMore[category.id]"
                    @load-more="loadMoreCategoryVideos(category.id)"
                />
            </template>
        </template>
    </div>
</template>

<script>
import { createSessionPagination } from '../utils/sessionPagination';
import { API } from 'aws-amplify';
import { track as analyticsTrack, captureError } from '../utils/analytics';
import ShortUniqueId from 'short-unique-id';
import {
    videosByStatusWithOwner,
    interactionByUserId,
    listCategoryModelsWithChildren,
    getCategoryPrimaryVideos,
    topVideosByStatus,
    usersBySubscribersCount,
    listTagModelsForAutocomplete,
    getUserModelByLegacyId,
    videosByOwnerIdAll
} from '../graphql/custom.js';
import { updateVideoModel, createTagModel, createVideoTags } from '../graphql/mutations.js';
import VideoGrid from '../components/VideoGrid.vue';
import ChannelGrid from '../components/ChannelGrid.vue';
import VideoForm from '../components/VideoForm.vue';
import VideoRecommendations from './VideoRecommendations.vue';
import VideoSection from './VideoSection.vue';
import FeaturedCarousel from './FeaturedCarousel.vue';
import ApiErrorState from './ApiErrorState.vue';
// import { isAuthenticated } from '../utils/auth.js';

const uid = new ShortUniqueId();

export default {
    name: 'main-view',
    components: {
        VideoGrid,
        ChannelGrid,
        VideoForm,
        VideoRecommendations,
        VideoSection,
        FeaturedCarousel,
        ApiErrorState
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' },
        isAdminRef: { from: 'isAdminRef', default: null }
    },
    data() {
        return {
            allVideos: null,
            allVideosNextToken: null,
            isLoadingMoreVideos: false,
            videoPageSize: 24,
            // Remembers which query/vars produced allVideos so Load More can
            // continue the same list (latest, top, etc.).
            videoPager: null,
            videoProgress: null,
            // On /top, a logged-in non-admin owner's own draft videos, merged into
            // the published top list by view count.
            ownerTopDrafts: [],
            allChannels: null,
            uploaded: false,
            uploading: false,
            uploadProgress: 0,
            isLoadingData: true,
            isLoadingRecent: true,
            isLoadingTop: true,
            isLoadingCategories: true,
            loadError: false,
            homeLoadError: false,
            created: false,
            creating: false,
            imageURL: null,
            currentUser: null,
            currentUserModel: null,
            isUserAuthenticated: false,
            categories: [],
            primaryCategory: null,
            secondaryCategory: null,
            videoTags: [], // For video tags
            availableTags: [], // For tag autocomplete
            userImages: [],
            recentVideos: [],
            topVideos: [],
            categoryVideos: {},
            categoryNextTokens: {},
            categoryLoadingMore: {},
            categoryProgress: {},
            allCategories: [],
            newVideo: {
                id: uid(),
                titleTA: '',
                titleEN: '',
                descriptionEN: '',
                descriptionTA: '',
                ownerId: null,
                videoModelPrimaryCategoryId: null,
                videoModelSecondaryCategoryId: null
            },
            categoryColors: ['#9c27b0', '#2196f3', '#009688', '#4caf50', '#ff5722', '#607d8b']
        }
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        isAdminViewer() {
            // unwrapInjectedRef is on (main.js), so the injected ref arrives unwrapped.
            // Named isAdminViewer because the global mixin's `isAdmin` data would shadow it.
            return this.isAdminRef === true;
        },
        showUploadSection() {
            return this.$route.path === '/upload';
        },
        columnCount() {
            return 4;
        },
        isHomePage() {
            return this.$route.path === '/';
        },
        isPopularChannelsPage() {
            return this.$route.path.includes('popular-channels');
        },
        // The grid list. When an owner's own drafts were pulled for /top, merge them
        // into the list and re-sort by views; otherwise pass allVideos through unchanged.
        displayedGridVideos() {
            if (this.ownerTopDrafts.length && Array.isArray(this.allVideos)) {
                const byId = new Map();
                this.allVideos.forEach(v => v && byId.set(v.id, v));
                this.ownerTopDrafts.forEach(v => { if (v && !byId.has(v.id)) byId.set(v.id, v); });
                return Array.from(byId.values()).sort((a, b) => (b.viewsCount || 0) - (a.viewsCount || 0));
            }
            return this.allVideos;
        }
    },
    watch: {
        // Admin state resolves asynchronously after load; refetch the homepage
        // sections so unpublished videos appear for admins.
        isAdminViewer(isAdmin) {
            if (isAdmin && this.isHomePage) this.loadHomepageData();
        },
        $route() {
            this.loadVideos();
            // Load homepage data when navigating to home page
            if (this.isHomePage) {
                this.loadHomepageData();
            }
        }
    },
    async created() {
        // Reset page title to default
        document.title = 'Eelam TV - Eelam Songs, Eelam Videos, Tamil History'

        await this.initializeUser();
        this.fetchCategories();
        this.loadVideos();

        // Load homepage data if on home page
        if (this.isHomePage) {
            this.loadHomepageData();
        }
    },
    methods: {
        async loadHomepageData() {
            this.homeLoadError = false;
            // Fetch all homepage sections in parallel
            await Promise.all([
                this.fetchRecentVideos(),
                this.fetchTopVideosForHome(),
                this.fetchFeaturedCategories()
            ]);
        },
        async fetchRecentVideos() {
            this.isLoadingRecent = true;
            try {
                // Admins also see unpublished videos; everyone else published only.
                const recentVariables = {
                    sortDirection: "DESC",
                    status: "ready",
                    limit: 8
                };
                if (!this.isAdminViewer) recentVariables.filter = { publishState: { eq: "published" } };
                const videosRes = await API.graphql({
                    query: videosByStatusWithOwner,
                    variables: recentVariables
                });
                let recentVideos = videosRes.data.videosByStatus.items;

                // If user is authenticated, fetch their processing videos and prepend
                const ownerId = this.currentUserModel?.id || this.currentUser?.sub;
                if (ownerId) {
                    try {
                        const allRes = await API.graphql({
                            query: videosByOwnerIdAll,
                            variables: { ownerId, limit: 20 }
                        });
                        const ownerVisibleVideos = (allRes.data.videosByOwner.items || [])
                            .filter(video => !this.isPublicVideo(video))
                            .filter(video => !recentVideos.some(recentVideo => recentVideo.id === video.id));
                        if (ownerVisibleVideos.length > 0) {
                            // Add owner info so VideoThumb can display avatar
                            const ownerStub = {
                                id: ownerId,
                                username: this.currentUserModel?.username || this.$parent?.currentUser?.model?.username || this.currentUser?.username || '',
                                displayName: this.currentUserModel?.displayName || this.$parent?.currentUser?.model?.displayName || '',
                                firstName: this.currentUserModel?.firstName || this.$parent?.currentUser?.model?.firstName || '',
                                lastName: this.currentUserModel?.lastName || this.$parent?.currentUser?.model?.lastName || '',
                                legacyAvatar: this.currentUserModel?.legacyAvatar || this.$parent?.currentUser?.model?.legacyAvatar || '',
                                status: this.currentUserModel?.status || this.$parent?.currentUser?.model?.status || 'unverified',
                            };
                            ownerVisibleVideos.forEach(v => {
                                const videoOwner = v.owner || {};
                                v.owner = {
                                    id: videoOwner.id || ownerStub.id,
                                    username: videoOwner.username || ownerStub.username,
                                    displayName: videoOwner.displayName || ownerStub.displayName,
                                    firstName: videoOwner.firstName || ownerStub.firstName,
                                    lastName: videoOwner.lastName || ownerStub.lastName,
                                    legacyAvatar: videoOwner.legacyAvatar || ownerStub.legacyAvatar,
                                    status: videoOwner.status || ownerStub.status,
                                };
                                v.isOwnerVisibleDraft = true;
                            });
                            recentVideos = [...ownerVisibleVideos, ...recentVideos];
                        }
                    } catch (e) {
                        console.log('Could not fetch owner draft videos:', e);
                    }
                }

                this.recentVideos = recentVideos;
                await this.fetchUserImagesForVideos(this.recentVideos);
            } catch (error) {
                console.error('Error fetching recent videos:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchRecentVideos' });
                this.recentVideos = [];
                this.homeLoadError = true;
            } finally {
                this.isLoadingRecent = false;
            }
        },
        async fetchTopVideosForHome() {
            this.isLoadingTop = true;
            try {
                const topVariables = {
                    sortDirection: "DESC",
                    status: "ready",
                    viewsCount: { gt: 0 },
                    limit: 8
                };
                if (!this.isAdminViewer) topVariables.filter = { publishState: { eq: "published" } };
                const videosRes = await API.graphql({
                    query: topVideosByStatus,
                    variables: topVariables
                });
                this.topVideos = videosRes.data.topVideosByStatus.items;
                await this.fetchUserImagesForVideos(this.topVideos);
            } catch (error) {
                console.error('Error fetching top videos:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchTopVideosForHome' });
                this.topVideos = [];
                this.homeLoadError = true;
            } finally {
                this.isLoadingTop = false;
            }
        },
        async fetchFeaturedCategories() {
            this.isLoadingCategories = true;
            try {
                const categoryRes = await API.graphql({
                    query: listCategoryModelsWithChildren,
                    variables: this.isAdminViewer ? { videoFilter: { status: { eq: 'ready' } } } : {}
                });
                const allCategoriesData = categoryRes.data.listCategoryModels.items;
                console.log('All categories fetched:', allCategoriesData);

                // Show only categories an admin has marked as featured. Featured
                // categories with no published videos render nothing (VideoSection
                // hides itself when empty), so no extra guard is needed here.
                this.allCategories = allCategoriesData.filter(cat => cat.isFeatured === true);
                console.log('Featured categories:', this.allCategories);

                // Seed categoryVideos from the included primaryVideos.
                for (const category of this.allCategories) {
                    this.categoryVideos[category.id] = (category.primaryVideos.items || [])
                        .filter(video => this.isVisibleToViewer(video));
                    // Remember where the first page ended so "Load more" can continue.
                    this.categoryNextTokens[category.id] = category.primaryVideos.nextToken || null;
                    this.categoryLoadingMore[category.id] = false;
                    this.categoryProgress[category.id] = createSessionPagination('home-category', category.id);
                    await this.fetchUserImagesForVideos(this.categoryVideos[category.id]);
                }
                this.categoryVideos = { ...this.categoryVideos };
                this.categoryNextTokens = { ...this.categoryNextTokens };

                // The list query's small per-category limit + the published filter
                // (applied after the index scan) often returns fewer than a full row
                // for draft-heavy categories, leaving the section looking empty. Top
                // each shown category up toward DISPLAY_TARGET in parallel.
                const DISPLAY_TARGET = 8;
                await Promise.all(this.allCategories.map(async (category) => {
                    const have = (this.categoryVideos[category.id] || []).length;
                    const token = this.categoryNextTokens[category.id];
                    if (have >= DISPLAY_TARGET || !token) return;
                    try {
                        const { collected, token: newToken } =
                            await this.collectCategoryBatch(category.id, token, DISPLAY_TARGET - have, 2);
                        if (collected.length) {
                            this.categoryVideos[category.id].push(...collected);
                            await this.fetchUserImagesForVideos(collected);
                        }
                        this.categoryNextTokens[category.id] = newToken;
                    } catch (e) {
                        console.error('Error topping up category videos:', category.id, e);
                    }
                }));
                this.categoryVideos = { ...this.categoryVideos };
                this.categoryNextTokens = { ...this.categoryNextTokens };
                await Promise.all(this.allCategories.map(category =>
                    this.categoryProgress[category.id].restore(() => this.loadMoreCategoryVideos(category.id))
                ));
            } catch (error) {
                console.error('Error fetching categories:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchFeaturedCategories' });
                this.allCategories = [];
                this.homeLoadError = true;
            } finally {
                this.isLoadingCategories = false;
            }
        },
        // Scans a category's primaryVideos GSI from `startToken`, collecting
        // published+ready videos not already loaded, until `target` are found or
        // `maxPages` are scanned. The published filter is applied after the index
        // scan, so a single page often yields few matches over a draft-heavy table.
        async collectCategoryBatch(categoryId, startToken, target, maxPages = 3) {
            const SCAN_LIMIT = 48;
            let token = startToken;
            let pages = 0;
            const collected = [];
            const existingIds = new Set((this.categoryVideos[categoryId] || []).map(v => v.id));

            while (token && collected.length < target && pages < maxPages) {
                const res = await API.graphql({
                    query: getCategoryPrimaryVideos,
                    variables: {
                        id: categoryId,
                        limit: SCAN_LIMIT,
                        nextToken: token,
                        ...(this.isAdminViewer ? { videoFilter: { status: { eq: 'ready' } } } : {})
                    }
                });
                const pv = res.data.getCategoryModel?.primaryVideos;
                if (!pv) break;
                for (const video of pv.items || []) {
                    if (this.isVisibleToViewer(video) && !existingIds.has(video.id)) {
                        collected.push(video);
                        existingIds.add(video.id);
                    }
                }
                token = pv.nextToken || null;
                pages += 1;
            }
            return { collected, token };
        },
        async loadMoreCategoryVideos(categoryId) {
            const startToken = this.categoryNextTokens[categoryId];
            if (!startToken || this.categoryLoadingMore[categoryId]) return;

            const progress = this.categoryProgress[categoryId];
            this.categoryLoadingMore = { ...this.categoryLoadingMore, [categoryId]: true };
            try {
                const { collected, token } = await this.collectCategoryBatch(categoryId, startToken, 8, 4);
                if (this.categoryProgress[categoryId] !== progress) return false;
                if (collected.length > 0) {
                    this.categoryVideos[categoryId].push(...collected);
                    this.categoryVideos = { ...this.categoryVideos };
                    await this.fetchUserImagesForVideos(collected);
                }
                this.categoryNextTokens = { ...this.categoryNextTokens, [categoryId]: token };
                progress.loaded();
                return true;
            } catch (error) {
                console.error('Error loading more category videos:', error);
            } finally {
                if (this.categoryProgress[categoryId] === progress) {
                    this.categoryLoadingMore = { ...this.categoryLoadingMore, [categoryId]: false };
                }
            }
        },
        fetchUserImagesForVideos(videos) {
            if (!videos || videos.length === 0) return;

            const userIds = [...new Set(videos.filter(v => v.owner && v.owner.id).map(v => v.owner.id))];
            for (const userId of userIds) {
                if (this.userImages.find(img => img.userId === userId)) continue;
                const signedURL = `https://cdn1.eelam.tv/public/images/user/${userId}/avatar.png`;
                this.userImages.push({ userId, signedURL });
            }
        },
        async initializeUser() {
            try {
                const user = await API.Auth.currentAuthenticatedUser();
                console.log('User authenticated:', user);
                this.currentUser = user.attributes;
                this.isUserAuthenticated = true;

                // Try to get UserModel ID from parent first
                if (this.$parent?.currentUser?.model?.id) {
                    this.currentUserModel = this.$parent.currentUser.model;
                    this.newVideo.ownerId = this.currentUserModel.id;
                    console.log('Main - using userId from parent:', this.newVideo.ownerId);
                } else {
                    // Fetch user model by legacyId if we have one
                    const legacyUserId = this.currentUser['custom:legacyUserId'];
                    if (legacyUserId) {
                        try {
                            const res = await API.graphql({
                                query: getUserModelByLegacyId,
                                variables: { legacyId: legacyUserId }
                            });
                            if (res.data.userByLegacyId?.items?.length > 0) {
                                this.currentUserModel = res.data.userByLegacyId.items[0];
                                this.newVideo.ownerId = this.currentUserModel.id;
                                console.log('Main - fetched user by legacyId:', this.newVideo.ownerId);
                            } else {
                                this.newVideo.ownerId = this.currentUser.sub;
                                console.log('Main - user not found, using Cognito sub:', this.newVideo.ownerId);
                            }
                        } catch (error) {
                            console.error('Main - error fetching user:', error);
                            this.newVideo.ownerId = this.currentUser.sub;
                        }
                    } else {
                        this.newVideo.ownerId = this.currentUser.sub;
                        console.log('Main - no legacyUserId, using Cognito sub:', this.newVideo.ownerId);
                    }
                }
            } catch (error) {
                console.error('Authentication error:', error);
                // Handle unauthenticated state
                this.currentUser = null;
                this.isUserAuthenticated = false;
            }
        },
        loadVideos() {
            // Reset paging; the fetch method for this route re-populates it below.
            this.allVideosNextToken = null;
            this.videoPager = null;
            this.isLoadingMoreVideos = false;
            this.videoProgress = createSessionPagination('feed', this.$route.path, this.currentUser?.sub || 'guest');
            this.ownerTopDrafts = [];
            this.loadError = false;
            const route = this.$route.path;
            if (route.includes('history')) {
                document.title = 'Watch History - Eelam TV'
                this.fetchHistory();
            } else if (route.includes('top')) {
                document.title = 'Top Videos - Eelam TV'
                this.fetchTopVideos();
            } else if (route.includes('popular-channels')) {
                document.title = 'Popular Channels - Eelam TV'
                this.fetchPopularChannels();
            } else if (route.includes('trending')) {
                document.title = 'Trending Videos - Eelam TV'
                this.fetchAllVideos();
            } else if (route.includes('latest')) {
                document.title = 'Latest Videos - Eelam TV'
                this.fetchAllVideos();
            } else if (route === '/popular') {
                document.title = 'Popular Videos - Eelam TV'
                this.fetchAllVideos();
            } else {
                document.title = 'Eelam TV - Eelam Songs, Eelam Videos, Tamil History'
                this.fetchAllVideos();
            }
        },
        async fetchCategories() {
            const categoryRes = await API.graphql({ query: listCategoryModelsWithChildren });
            this.categories = categoryRes.data.listCategoryModels.items;
            const tagRes = await API.graphql({ query: listTagModelsForAutocomplete });
            this.availableTags = tagRes.data.listTagModels.items;
        },
        async fetchAllVideos() {
            this.setLoadingState(true);
            const progress = this.videoProgress;
            try {
                console.log('Fetching videos...');
                const variables = {
                    sortDirection: "DESC",
                    status: "ready",
                    filter: { publishState: { eq: "published" } },
                    limit: this.videoPageSize
                };
                const videosRes = await API.graphql({ query: videosByStatusWithOwner, variables });
                console.log('Videos response:', videosRes);
                if (this.videoProgress !== progress) return;
                this.allVideos = videosRes.data.videosByStatus.items;
                this.allVideosNextToken = videosRes.data.videosByStatus.nextToken || null;
                this.videoPager = { query: videosByStatusWithOwner, variables, resultKey: 'videosByStatus' };
                console.log('Videos loaded:', this.allVideos);
                await this.fetchUserImages();
                await progress.restore(() => this.videoProgress === progress && this.loadMoreVideos());
            } catch (error) {
                if (this.videoProgress !== progress) return;
                console.error('Error fetching videos:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchAllVideos' });
                this.allVideos = [];
                this.allVideosNextToken = null;
                this.videoPager = null;
                this.loadError = true;
            } finally {
                if (this.videoProgress === progress) this.setLoadingState(false);
            }
        },
        async loadMoreVideos() {
            if (!this.allVideosNextToken || this.isLoadingMoreVideos || !this.videoPager) return;

            const progress = this.videoProgress;
            this.isLoadingMoreVideos = true;
            try {
                const { query, variables, resultKey } = this.videoPager;
                const videosRes = await API.graphql({
                    query,
                    variables: { ...variables, nextToken: this.allVideosNextToken }
                });
                if (this.videoProgress !== progress) return false;
                const connection = videosRes.data[resultKey];
                const newVideos = connection?.items || [];
                if (newVideos.length > 0) {
                    this.allVideos.push(...newVideos);
                    this.fetchUserImagesForVideos(newVideos);
                }
                this.allVideosNextToken = connection?.nextToken || null;
                progress.loaded();
                return true;
            } catch (error) {
                console.error('Error loading more videos:', error);
                return false;
            } finally {
                if (this.videoProgress === progress) this.isLoadingMoreVideos = false;
            }
        },
        async fetchHistory() {
            this.setLoadingState(true);
            const userId = this.currentUserModel?.id || this.$parent?.currentUser?.model?.id || this.currentUser?.sub;
            console.log('fetchHistory - using userId:', userId);

            if (!userId) {
                console.log('fetchHistory - no userId available');
                this.allVideos = [];
                this.setLoadingState(false);
                return;
            }

            try {
                const videosRes = await API.graphql({
                    query: interactionByUserId,
                    variables: {
                        userId: userId,
                        sortDirection: "DESC",
                        limit: 100,
                    }
                });
                // Deduplicate by video ID
                const seenIds = new Set();
                const uniqueVideos = [];
                for (const item of videosRes.data.interactionByUserId.items) {
                    if (item.video && !seenIds.has(item.video.id)) {
                        seenIds.add(item.video.id);
                        uniqueVideos.push(item.video);
                    }
                }
                this.allVideos = uniqueVideos.filter(video => this.isPublicVideo(video));
                console.log('fetchHistory - found', this.allVideos.length, 'videos', this.allVideos);
                await this.fetchUserImages();
            } catch (error) {
                console.error('fetchHistory - error:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchHistory' });
                this.allVideos = [];
                this.loadError = true;
            } finally {
                this.setLoadingState(false);
            }
        },
        async fetchTopVideos() {
            this.setLoadingState(true);
            const progress = this.videoProgress;
            try {
                const isAdmin = !!this.$parent?.isAdmin;
                const ownerId = this.currentUserModel?.id || this.$parent?.currentUser?.model?.id || this.currentUser?.sub || null;

                const variables = {
                    sortDirection: "DESC",
                    status: "ready",
                    viewsCount: { gt: 0 },
                    limit: this.videoPageSize
                };
                // Admins see every video by views (drafts included) — no publish filter.
                // Everyone else gets the public list; owners get their own drafts merged
                // in separately (see fetchOwnerTopDrafts) to avoid pulling others' drafts.
                if (!isAdmin) {
                    variables.filter = { publishState: { eq: "published" } };
                }

                const videosRes = await API.graphql({ query: topVideosByStatus, variables });
                if (this.videoProgress !== progress) return;
                this.allVideos = videosRes.data.topVideosByStatus.items;
                this.allVideosNextToken = videosRes.data.topVideosByStatus.nextToken || null;
                this.videoPager = { query: topVideosByStatus, variables, resultKey: 'topVideosByStatus' };
                await this.fetchUserImages();
                await progress.restore(() => this.videoProgress === progress && this.loadMoreVideos());

                if (this.videoProgress === progress && !isAdmin && ownerId) {
                    await this.fetchOwnerTopDrafts(ownerId);
                }
            } catch (error) {
                if (this.videoProgress !== progress) return;
                console.error('Error fetching top videos:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchTopVideos' });
                this.allVideos = [];
                this.allVideosNextToken = null;
                this.videoPager = null;
                this.loadError = true;
            } finally {
                if (this.videoProgress === progress) this.setLoadingState(false);
            }
        },
        async fetchOwnerTopDrafts(ownerId) {
            try {
                const res = await API.graphql({
                    query: videosByOwnerIdAll,
                    variables: { ownerId, limit: 100 }
                });
                const drafts = (res.data.videosByOwner?.items || [])
                    .filter(video => !this.isPublicVideo(video) && (video.viewsCount || 0) > 0);
                this.ownerTopDrafts = drafts;
                await this.fetchUserImagesForVideos(drafts);
            } catch (error) {
                console.error('Error fetching owner draft top videos:', error);
                this.ownerTopDrafts = [];
            }
        },
        async fetchPopularChannels() {
            this.setLoadingState(true);
            try {
                const videosRes = await API.graphql({
                    query: usersBySubscribersCount,
                    variables: {
                        sortDirection: "DESC",
                        status: "verified",
                        limit: 50
                    }
                });
                this.allChannels = videosRes.data.usersBySubscribersCount.items;
                console.log('Popular channels loaded:', this.allChannels.length);
            } catch (error) {
                console.error('Error fetching popular channels:', error);
                captureError(error, { source: 'api_fetch', component: 'Main', operation: 'fetchPopularChannels' });
                this.allChannels = [];
                this.loadError = true;
            }
            this.setLoadingState(false);
        },
        setLoadingState(isLoading) {
            this.isLoadingData = isLoading;
        },
        fetchUserImages() {
            if (!this.allVideos) return;

            const userIds = [...new Set(this.allVideos.filter(it => it.owner && it.owner.id).map(it => it.owner.id))];
            for (const userId of userIds) {
                const signedURL = `https://cdn1.eelam.tv/public/images/user/${userId}/avatar.png`;
                this.userImages.push({ userId, signedURL });
            }
        },
        isPublishedVideo(video) {
            return !video?.publishState || video.publishState === 'published';
        },
        // What the current viewer may see in homepage sections: published for
        // everyone, everything for admins (VideoThumb labels unpublished cards).
        isVisibleToViewer(video) {
            return this.isAdminViewer || this.isPublishedVideo(video);
        },
        isPublicVideo(video) {
            return this.isPublishedVideo(video) &&
                (!video?.status || ['ready', 'processingReady'].includes(video.status));
        },
        isVerifiedCurrentUser() {
            const userModel = this.currentUserModel || this.$parent?.currentUser?.model;
            return userModel?.status === 'verified';
        },
        canPublishUploadsImmediately() {
            return Boolean(this.$parent?.isAdmin || this.isVerifiedCurrentUser());
        },
        getUploadPublishState() {
            return this.canPublishUploadsImmediately() ? 'published' : 'pending';
        },
        updateNewVideo(updates) {
            this.newVideo = { ...this.newVideo, ...updates };
        },
        updatePrimaryCategory(category) {
            this.primaryCategory = category;
        },
        updateSecondaryCategory(category) {
            this.secondaryCategory = category;
        },
        updateVideoTags(tags) {
            this.videoTags = [...tags];
        },
        async saveVideo() {
            this.creating = true;
            try {
                const userId = this.$parent?.currentUser?.model?.id || this.currentUser.sub;
                const videoData = {
                    ...this.newVideo,
                    publishState: this.getUploadPublishState(),
                    videoModelPrimaryCategoryId: this.primaryCategory?.id,
                    videoModelSecondaryCategoryId: this.secondaryCategory?.id,
                    owner: userId // Add owner field for authorization
                };

                analyticsTrack('video_publish_start', {
                    video_id: videoData.id,
                    mode: 'create',
                    category_id: videoData.videoModelPrimaryCategoryId,
                    secondary_category_id: videoData.videoModelSecondaryCategoryId,
                    tag_count: this.videoTags.length,
                    publish_state: videoData.publishState
                });

                await API.graphql({
                    query: updateVideoModel,
                    variables: { input: videoData },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });

                // Save video tags
                await this.saveVideoTags(videoData.id, this.videoTags);

                analyticsTrack('video_search_index_update', {
                    video_id: videoData.id,
                    action: 'upsert',
                    mode: 'create',
                    success: true,
                    source: 'video_stream_lambda'
                });

                analyticsTrack('video_publish', {
                    video_id: videoData.id,
                    mode: 'create',
                    category_id: videoData.videoModelPrimaryCategoryId,
                    secondary_category_id: videoData.videoModelSecondaryCategoryId,
                    tag_count: this.videoTags.length,
                    publish_state: videoData.publishState
                });
                this.resetVideoForm();
            } catch (error) {
                console.error('Error publishing video:', error);
                analyticsTrack('video_publish_failed', {
                    video_id: this.newVideo?.id,
                    mode: 'create',
                    error_code: this.getAnalyticsErrorCode(error)
                });
                this.$toast?.error?.('Failed to publish video. Please try again.');
            } finally {
                this.creating = false;
            }
        },
        getAnalyticsErrorCode(error) {
            if (error?.code) return error.code;
            const message = String(error?.errors?.[0]?.message || error?.message || error?.name || '').toLowerCase();
            if (!message) return 'unknown';
            if (message.includes('not authorized') || message.includes('unauthorized')) return 'unauthorized';
            if (message.includes('network') || message.includes('fetch')) return 'network_error';
            if (message.includes('timeout') || message.includes('timed out')) return 'timeout';
            if (message.includes('validation')) return 'validation_error';
            return 'error';
        },
        async saveVideoTags(videoId, tags) {
            try {
                // Create new tags that don't exist yet
                const newTags = tags.filter(tag => tag.id.startsWith('temp-'));
                for (const tag of newTags) {
                    try {
                        const createTagRes = await API.graphql({
                            query: createTagModel,
                            variables: {
                                input: {
                                    label: tag.label,
                                    createdAt: new Date().toISOString(),
                                    updatedAt: new Date().toISOString()
                                }
                            },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                        
                        // Create video-tag association
                        await API.graphql({
                            query: createVideoTags,
                            variables: {
                                input: {
                                    videoModelID: videoId,
                                    tagModelID: createTagRes.data.createTagModel.id
                                }
                            },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                    } catch (error) {
                        console.error('Error creating tag:', error);
                    }
                }
                
                // Add existing tag associations
                const existingTags = tags.filter(tag => !tag.id.startsWith('temp-'));
                for (const tag of existingTags) {
                    try {
                        await API.graphql({
                            query: createVideoTags,
                            variables: {
                                input: {
                                    videoModelID: videoId,
                                    tagModelID: tag.id
                                }
                            },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                    } catch (error) {
                        console.error('Error adding tag association:', error);
                    }
                }
            } catch (error) {
                console.error('Error saving video tags:', error);
            }
        },
        resetVideoForm() {
            this.created = true;
            this.uploaded = false;
            this.videoTags = [];
            const userId = this.$parent?.currentUser?.model?.id || this.currentUser?.sub;
            this.newVideo = {
                id: uid(),
                titleTA: '',
                titleEN: '',
                descriptionEN: '',
                descriptionTA: '',
                ownerId: userId,
                videoModelPrimaryCategoryId: null,
                videoModelSecondaryCategoryId: null
            };
        }
    }
}
</script>

<style scoped>
.main-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px 0;
}

.shimmer-video-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding: 20px;
}

.load-more-container {
    display: flex;
    justify-content: center;
    padding: 32px 20px;
}

.load-more-button {
    background: var(--accent-primary);
    color: white;
    border: none;
    padding: 12px 28px;
    border-radius: 25px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.load-more-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--shadow-color);
}

.load-more-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-tertiary);
    font-size: 14px;
}

.load-more-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--border-color);
    border-top-color: var(--accent-primary);
    border-radius: 50%;
    animation: main-spin 0.8s linear infinite;
}

@keyframes main-spin {
    to { transform: rotate(360deg); }
}

.shimmer-channels-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
    padding: 20px;
}

@media (max-width: 1200px) {
    .shimmer-video-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 992px) {
    .shimmer-video-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .shimmer-video-grid {
        grid-template-columns: 1fr;
    }
}

.upload-field {
    margin: 20px 0;
    padding: 20px;
    border: 2px dashed var(--border-color);
    border-radius: 8px;
    text-align: center;
    background: var(--bg-tertiary);
}

.upload-button {
    margin: 10px 0;
}

.upload-status {
    color: var(--text-tertiary);
    font-style: italic;
}

.upload-preview {
    color: var(--accent-primary);
    font-weight: bold;
}

.flex {
    display: flex;
}

.auth-required {
    text-align: center;
    padding: 60px 20px;
    max-width: 500px;
    margin: 0 auto;
}

.auth-icon {
    margin-bottom: 20px;
    color: var(--accent-danger);
}

.auth-required h3 {
    color: var(--text-secondary);
    margin-bottom: 15px;
    font-size: 24px;
}

.auth-required p {
    color: var(--text-tertiary);
    margin-bottom: 30px;
    font-size: 16px;
    line-height: 1.5;
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    transition: background 0.3s;
}

.btn-primary {
    background: var(--accent-primary);
    color: white;
}

.btn-primary:hover {
    background: var(--accent-hover);
}

/* Create Video Upload Section */
.create-video {
    background: var(--bg-card);
    border-radius: 12px;
    padding: 30px;
    margin: 20px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.create-video h2 {
    font-size: 24px;
    font-weight: 600;
    color: var(--text-secondary);
    margin: 0 0 20px 0;
}

@media (max-width: 768px) {
    .main-container {
        padding: 10px 0;
    }

    .create-video {
        margin: 10px;
        padding: 20px;
    }
}

</style>
  
